import {
  apiAuthenticatedRequest,
  apiPublicRequest,
  isApiConfigured,
} from '@/services/api/apiClient';
import type {
  HealthData,
  VersionData,
  WorkerSessionPayload,
} from '@/types/api';

export type WorkerConnectivityResult = {
  configured: boolean;
  health: HealthData | null;
  version: VersionData | null;
  healthError: string | null;
  versionError: string | null;
};

/** GET /health — sin autenticación. */
export function fetchWorkerHealth(): Promise<HealthData> {
  return apiPublicRequest<HealthData>('/health');
}

/** GET /version — sin autenticación. */
export function fetchWorkerVersion(): Promise<VersionData> {
  return apiPublicRequest<VersionData>('/version');
}

/** GET /auth/session — JWT verificado + rol desde Firestore en el Worker. */
export function fetchWorkerSession(
  forceRefreshToken = false,
): Promise<WorkerSessionPayload> {
  return apiAuthenticatedRequest<WorkerSessionPayload>('/auth/session', {
    forceRefreshToken,
  });
}

/** GET /version con Bearer (diagnóstico de contrato auth). */
export function fetchWorkerVersionAuthenticated(
  forceRefreshToken = false,
): Promise<VersionData> {
  return apiAuthenticatedRequest<VersionData>('/version', {
    forceRefreshToken,
  });
}

/** Valida URL configurada y endpoints públicos (para consola admin / diagnóstico). */
export async function probeWorkerConnectivity(): Promise<WorkerConnectivityResult> {
  if (!isApiConfigured()) {
    return {
      configured: false,
      health: null,
      version: null,
      healthError: 'VITE_API_BASE_URL no configurada',
      versionError: null,
    };
  }

  const [healthSettled, versionSettled] = await Promise.allSettled([
    fetchWorkerHealth(),
    fetchWorkerVersion(),
  ]);

  return {
    configured: true,
    health:
      healthSettled.status === 'fulfilled' ? healthSettled.value : null,
    version:
      versionSettled.status === 'fulfilled' ? versionSettled.value : null,
    healthError:
      healthSettled.status === 'rejected'
        ? reasonMessage(healthSettled.reason)
        : null,
    versionError:
      versionSettled.status === 'rejected'
        ? reasonMessage(versionSettled.reason)
        : null,
  };
}

function reasonMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  return String(reason);
}
